// 🔎 Recherche du nouveau design (tour 34) : les filtres vivent dans l'adresse, pour partager une
// recherche. ?subject=<id ou nom>&level=<id>&categories=<id,id>&online=1&offers=1&budget=30&sort=rating
// Un nom de matière (« ?subject=Mathématiques », liens de l'accueil) est accepté, comme avant.
// &family=lycee : une famille de niveaux, tous ses niveaux (tour 40) ; un niveau précis l'emporte.
// Famille sans niveau visible dans la liste du site (tour 42) : ignorée (tous les niveaux).
// &domains=<id,id> : domaines de la matière (catégories sur deux niveaux, tour 56), lus par la page
// seulement interrupteur allumé, comme les catégories seulement interrupteur coupé.
import { FAMILIES, familyHasLevels } from "../../../helper/levelFamilies";

export const SORTS = { relevance: 2, rating: 1, price: 3, hours: 4 };
const SORT_KEYS = Object.keys(SORTS);
const isId = (s) => /^[a-f0-9]{24}$/i.test(String(s || ""));
const plain = (s) => String(s || "").normalize("NFD").replace(/[̀-ͯ]/g, "").trim().toLowerCase();

// Matière de l'adresse : identifiant connu, ou nom français ou anglais (sans accents ni majuscules)
export const subjectFrom = (value, subjects = []) => {
  if (!value) return null;
  if (isId(value)) return subjects.find((s) => String(s._id) === value) || { _id: value };
  return subjects.find((s) => [s.subject_name_fr, s.subject_name].some((n) => plain(n) === plain(value))) || null;
};

// Adresse → filtres (grades : la liste des niveaux du site, pour ignorer une famille sans niveau)
export const parseQuery = (search, subjects = [], grades = []) => {
  const p = new URLSearchParams(search || "");
  const budget = Number(p.get("budget"));
  return {
    subjectId: subjectFrom(p.get("subject"), subjects)?._id || null,
    levelId: isId(p.get("level")) ? p.get("level") : null,
    family: !isId(p.get("level")) && FAMILIES.includes(p.get("family")) && familyHasLevels(grades, p.get("family")) ? p.get("family") : null,
    categories: (p.get("categories") || "").split(",").filter(isId),
    domains: (p.get("domains") || "").split(",").filter(isId),
    online: p.get("online") === "1",
    offers: p.get("offers") === "1",
    budget: Number.isFinite(budget) && budget > 0 ? Math.round(budget) : null,
    sort: SORT_KEYS.includes(p.get("sort")) ? p.get("sort") : "relevance",
  };
};

// Filtres → adresse (seulement ce qui s'écarte des valeurs par défaut)
export const buildQuery = ({ subjectId, levelId, family, categories = [], domains = [], online, offers, budget, sort } = {}) => {
  const p = new URLSearchParams();
  if (subjectId) p.set("subject", subjectId);
  if (levelId) p.set("level", levelId);
  else if (family) p.set("family", family);
  if (categories.length) p.set("categories", categories.join(","));
  if (domains.length) p.set("domains", domains.join(","));
  if (online) p.set("online", "1");
  if (offers) p.set("offers", "1");
  if (budget) p.set("budget", String(budget));
  if (sort && sort !== "relevance") p.set("sort", sort);
  const s = p.toString();
  return s ? `?${s}` : "";
};

// Filtres → corps de la recherche paginée de l'API (services/AdvertSearchService.js) ; offres
// seulement si l'interrupteur « offers » est allumé
export const requestOf = (q, { page = 1, limit = 12, offersOn = false } = {}) => ({
  page,
  limit,
  sort: SORTS[q.sort] || SORTS.relevance,
  ...(q.subjectId ? { subject_id: q.subjectId } : {}),
  ...(q.levelId ? { level_id: q.levelId } : q.family ? { family: q.family } : {}),
  ...(q.categories?.length ? { associated: q.categories } : {}),
  ...(q.domains?.length ? { domains: q.domains } : {}),
  ...(q.online ? { online_only: true } : {}),
  ...(offersOn && q.offers ? { only_offers: true } : {}),
  ...(q.budget ? { max_rate: q.budget } : {}),
});

// Un filtre au moins, en plus de la matière et du niveau
export const hasFilters = (q) => !!(q.categories?.length || q.domains?.length || q.online || q.offers || q.budget || (q.sort && q.sort !== "relevance"));

// « de mathématiques », « d'informatique », « de physique-chimie » (français) ; le nom seul en
// anglais. Chaque mot en minuscules, sauf les sigles (« SVT »)
const lowerWord = (word) => (/^.[a-zà-ÿ]/.test(word) ? word.charAt(0).toLowerCase() + word.slice(1) : word);
const lowerFirst = (name) => name.replace(/[^\s'’()-]+/g, lowerWord);
export const subjectInTitle = (name, language = "fr") => {
  if (!name) return "";
  if (language === "en") return name;
  const lower = lowerFirst(name);
  return /^[aeiouyhàâäéèêëîïôöûùü]/i.test(lower) ? `d'${lower}` : `de ${lower}`;
};
export const levelInTitle = (name, language = "fr") => (language === "en" || !name ? name || "" : lowerFirst(name));
